import { BacklogStatus } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';

type Backlog = TrpcOutputs['isQuoteBacklogged']['backlogs'][0];

const isActiveBacklog = (backlog: Pick<Backlog, 'starts_at' | 'expires_at'>) => {
  const now = Date.now();
  if (new Date(backlog.starts_at).getTime() > now) return false;
  if (!backlog.expires_at) return true;
  return new Date(backlog.expires_at).getTime() > now;
};

export const useBacklogs = (quoteId: number, providerId?: string | null, airports: string[] = []) => {
  const [backlogs, setBacklogs] = useState<Backlog[]>([]);
  const {
    data,
    isLoading,
    error,
  } = trpc.isQuoteBacklogged.useQuery({ id: quoteId }, { refetchOnWindowFocus: false, enabled: !!quoteId });

  useEffect(() => {
    if (data) {
      setBacklogs(data.backlogs);
    }
  }, [data]);

  trpc.onBacklogUpdate.useSubscription(
    { provider_id: providerId ?? '', airports },
    {
      enabled: !!providerId,
      onData: (incoming) => {
        setBacklogs((prev) => {
          const without = prev.filter((b) => b.id !== incoming.id);
          if (!isActiveBacklog(incoming)) return without;
          return [...without, incoming];
        });
      },
      onError: (err) => {
        console.log('onBacklogUpdate.error: ', err);
      },
    },
  );

  const { backlogged, atRisk } = useMemo(
    () => ({
      backlogged: backlogs.filter((b) => b.status === BacklogStatus.Backlogged),
      atRisk: backlogs.filter((b) => b.status === BacklogStatus.AtRisk),
    }),
    [backlogs],
  );

  return { backlogs, backlogged, atRisk, isLoading, error: error?.message };
};
